import { Metric } from '../../../../dashboard-config/dashboard-config.model';
import { DossierShellMetricProfile } from '../create-dossier-states-definition';
import { DossierValueColumn } from '../dossier-shell.profile';
import {
  DossierMetricProfile,
  dossierEconomicParticipantProfile,
  dossierEmployedProfile,
  dossierGvaProfile,
  resolveDossierHeadingLabel,
  resolveDossierHeadingLabels,
  dossierRevenueProfile,
  dossierTaxedProfile,
} from '../dossier-metric.profile';
import {
  DashboardDossierStatesInfoFieldConfig,
  DashboardDossierStatesInfoViewConfig,
} from './dashboard-dossier-states-info.model';
import {
  companyTypeLabel,
  economicParticipantType,
  economicParticipantTypeLabel,
  EconomicParticipantType,
  employedType,
  employedTypeLabel,
  GrossValueAddedType,
  grossValueAddedType,
  grossValueAddedTypeLabel,
  rowValue,
} from './dashboard-dossier-states-info.view';

/**
 * Info-shell metric profile: shared dossier metric core +
 * info view.
 */
export type DossierInfoMetricProfile = DossierShellMetricProfile<
  DashboardDossierStatesInfoViewConfig
>;

const economicParticipantDetailsByType: Record<
  EconomicParticipantType,
  DashboardDossierStatesInfoFieldConfig[]
> = {
  total: [
    { label: 'davon Unternehmen', valueFrom: 'companies_count', format: 'number' },
    {
      label: 'davon Kleinunternehmen',
      valueFrom: 'small_companies_count',
      format: 'number',
    },
    {
      label: 'davon Gesamtbeschäftigte',
      valueFrom: 'employed_total',
      format: 'number',
    },
  ],
  core_market: [
    { label: 'davon Unternehmen', valueFrom: 'companies_count', format: 'number' },
    {
      label: 'davon Sv-pflichtig Beschäftigte',
      valueFrom: 'employed_social_security',
      format: 'number',
    },
  ],
  marginal: [
    {
      label: 'davon Kleinunternehmen',
      valueFrom: 'small_companies_count',
      format: 'number',
    },
    {
      label: 'davon Geringfügig Beschäftigte',
      valueFrom: 'employed_marginal',
      format: 'number',
    },
  ],
};

function fromShared(
  shared: DossierMetricProfile,
  shell: {
    valueColumns: DossierValueColumn[];
    view: DashboardDossierStatesInfoViewConfig;
  },
): DossierInfoMetricProfile {
  return {
    ...shared,
    ...shell,
  };
}

export const dossierInfoTaxedProfile: DossierInfoMetricProfile =
  fromShared(dossierTaxedProfile, {
    valueColumns: [
      { measure: 'value', resultColumn: 'value' },
      { measure: 'value', resultColumn: 'revenue__k_eur', fromMetric: Metric.CompaniesRevenue },
      { measure: 'stateShareOfCountryPct', resultColumn: 'count_state_country_pct' },
      { measure: 'stateShareOfCountryPct', resultColumn: 'revenue_state_country_pct', fromMetric: Metric.CompaniesRevenue },
      { measure: 'topSubMarketId', resultColumn: 'top_sub_market_id' },
      { measure: 'topSubMarketValue', resultColumn: 'top_sub_market_value' },
    ],
    view: {
      metric: {
        label: 'Kennzahl',
        valueFrom: ({ context }) => companyTypeLabel(context),
      },
      primary: {
        label: ({ context }) => `Anzahl ${companyTypeLabel(context)}`,
        valueFrom: 'value',
        format: 'number',
        emphasis: 'strong',
      },
      details: [
        {
          label: 'Erzielte Umsätze',
          valueFrom: 'revenue__k_eur',
          format: 'currency',
        },
      ],
      shares: [
        {
          label: ({ row }) =>
            `${row['state_code']}-Unternehmensanteil an Deutschland`,
          valueFrom: 'count_state_country_pct',
          format: 'percent',
        },
        {
          label: ({ row }) =>
            `${row['state_code']}-Umsatzanteil an Deutschland`,
          valueFrom: 'revenue_state_country_pct',
          format: 'percent',
        },
      ],
      topMarket: {
        valueFrom: 'top_sub_market_value',
        format: 'number',
        description: ({ row, context }) =>
          `Nach Anzahl an ${companyTypeLabel(context)} in der ${row['sum_sub_market_name']}`,
      },
    },
  });

export const dossierInfoRevenueProfile: DossierInfoMetricProfile =
  fromShared(dossierRevenueProfile, {
    valueColumns: [
      { measure: 'value', resultColumn: 'value' },
      { measure: 'value', resultColumn: 'count', fromMetric: Metric.CompaniesCount },
      { measure: 'stateShareOfCountryPct', resultColumn: 'count_state_country_pct', fromMetric: Metric.CompaniesCount },
      { measure: 'stateShareOfCountryPct', resultColumn: 'revenue_state_country_pct' },
      { measure: 'topSubMarketId', resultColumn: 'top_sub_market_id' },
      { measure: 'topSubMarketValue', resultColumn: 'top_sub_market_value' },
    ],
    view: {
      metric: {
        label: 'Kennzahl',
        valueFrom: ({ context }) => companyTypeLabel(context),
      },
      primary: {
        label: 'Erzielte Umsätze',
        valueFrom: 'value',
        format: 'currency',
        emphasis: 'strong',
      },
      details: [
        {
          label: ({ context }) => `Anzahl ${companyTypeLabel(context)}`,
          valueFrom: 'count',
          format: 'number',
        },
      ],
      shares: [
        {
          label: ({ row }) =>
            `${row['state_code']}-Umsatzanteil an Deutschland`,
          valueFrom: 'revenue_state_country_pct',
          format: 'percent',
        },
        {
          label: ({ row }) =>
            `${row['state_code']}-Unternehmensanteil an Deutschland`,
          valueFrom: 'count_state_country_pct',
          format: 'percent',
        },
      ],
      topMarket: {
        valueFrom: 'top_sub_market_value',
        format: 'number',
        description: ({ row, context }) =>
          `Nach Höhe der Umsätze von ${companyTypeLabel(context)} in der ${row['sum_sub_market_name']}`,
      },
    },
  });

const gvaInfoTextsByType: Record<
  GrossValueAddedType,
  { primaryLabel: string; shareLabel: string }
> = {
  gross_value_added: {
    primaryLabel: 'Erzielte Bruttowertschöpfung',
    shareLabel: 'Bruttowertschöpfungsanteil',
  },
  production_value: {
    primaryLabel: 'Erzielter Produktionswert',
    shareLabel: 'Produktionswertanteil',
  },
};

export const dossierInfoGvaProfile: DossierInfoMetricProfile =
  fromShared(dossierGvaProfile, {
    valueColumns: [
      { measure: 'value', resultColumn: 'value' },
      { measure: 'stateShareOfCountryPct', resultColumn: 'value_state_country_pct' },
      { measure: 'topSubMarketId', resultColumn: 'top_sub_market_id' },
      { measure: 'topSubMarketValue', resultColumn: 'top_sub_market_value' },
    ],
    view: {
      metric: {
        label: 'Kennzahl',
        valueFrom: ({ context }) => grossValueAddedTypeLabel(context),
      },
      primary: {
        label: ({ context }) =>
          gvaInfoTextsByType[grossValueAddedType(context)].primaryLabel,
        valueFrom: 'value',
        format: 'currency',
        emphasis: 'strong',
      },
      details: [],
      shares: [
        {
          label: ({ row, context }) =>
            `${row['state_code']}-${gvaInfoTextsByType[grossValueAddedType(context)].shareLabel} an Deutschland`,
          valueFrom: 'value_state_country_pct',
          format: 'percent',
        },
      ],
      topMarket: {
        valueFrom: 'top_sub_market_value',
        format: 'currency',
        description: ({ row, context }) =>
          `Nach Höhe ${resolveDossierHeadingLabel(
            resolveDossierHeadingLabels(dossierGvaProfile, grossValueAddedTypeLabel(context)),
            'genitive',
          )} in der ${row['sum_sub_market_name']}`,
      },
    },
  });

export const dossierInfoEconomicParticipantProfile: DossierInfoMetricProfile =
  fromShared(dossierEconomicParticipantProfile, {
    valueColumns: [
      { measure: 'value', resultColumn: 'value' },
      { measure: 'topSubMarketId', resultColumn: 'top_sub_market_id' },
      { measure: 'topSubMarketValue', resultColumn: 'top_sub_market_value' },
    ],
    view: {
      metric: {
        label: 'Kennzahl',
        valueFrom: ({ context }) => economicParticipantTypeLabel(context),
      },
      primary: {
        label: ({ context }) =>
          `Anzahl ${economicParticipantTypeLabel(context)}`,
        valueFrom: 'value',
        format: 'number',
        emphasis: 'strong',
      },
      details: ({ context }) =>
        economicParticipantDetailsByType[economicParticipantType(context)],
      showDetailArrows: true,
      shares: [
        {
          label: ({ row, context }) =>
            `${row['state_code']}-${economicParticipantTypeLabel(context)}-Anteil an Deutschland`,
          valueFrom: ({ row, context }) => {
            const type = economicParticipantType(context);
            return rowValue(
              row,
              `economic_participants_${type}_state_country_pct`,
              `economic_participants_${type}_state_pct`,
              'value_state_pct',
            );
          },
          format: 'percent',
        },
      ],
      topMarket: {
        valueFrom: 'top_sub_market_value',
        format: 'number',
        description: ({ row, context }) =>
          `Nach Anzahl der ${economicParticipantTypeLabel(context)} in der ${row['sum_sub_market_name']}`,
      },
    },
  });

export const dossierInfoEmployedProfile: DossierInfoMetricProfile =
  fromShared(dossierEmployedProfile, {
    valueColumns: [
      { measure: 'value', resultColumn: 'value' },
      { measure: 'topSubMarketId', resultColumn: 'top_sub_market_id' },
      { measure: 'topSubMarketValue', resultColumn: 'top_sub_market_value' },
    ],
    view: {
      metric: {
        label: 'Kennzahl',
        valueFrom: ({ context }) => employedTypeLabel(context),
      },
      primary: {
        label: ({ context }) => `Anzahl ${employedTypeLabel(context)}`,
        valueFrom: 'value',
        format: 'number',
        emphasis: 'strong',
      },
      details: ({ context }) =>
        employedType(context) === 'total'
          ? [
              {
                label: 'davon Sv-pflichtig Beschäftigte',
                valueFrom: 'employed_social_security',
                format: 'number',
              },
              {
                label: 'davon Geringfügig Beschäftigte',
                valueFrom: 'employed_marginal',
                format: 'number',
              },
            ]
          : [],
      showDetailArrows: true,
      shares: [
        {
          label: ({ row, context }) =>
            `${row['state_code']}-${employedTypeLabel(context)}-Anteil an Deutschland`,
          valueFrom: ({ row, context }) => {
            const type = employedType(context);
            return rowValue(
              row,
              `employed_${type}_state_country_pct`,
              `employed_${type}_state_pct`,
              'value_state_pct',
            );
          },
          format: 'percent',
        },
      ],
      topMarket: {
        valueFrom: 'top_sub_market_value',
        format: 'number',
        description: ({ row, context }) =>
          `Nach Anzahl der ${employedTypeLabel(context)} in der ${row['sum_sub_market_name']}`,
      },
    },
  });
